import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Variants ported from FE bk (.badge-* classes in index.css). Three
// product-category tints (profile/idphoto/corporate) + five semantic
// pastels (green/red/blue/yellow/purple) + the legacy shadcn set.
//
// Tokens come from src/styles/tokens.css; the {bg, text} pair makes the
// badge readable on the warm cream surface. Use Tailwind v4 arbitrary
// `[property:value]` syntax to bind directly to the CSS variables
// without adding new utility classes.
const badgeVariants = cva(
  "inline-flex items-center rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "border-border text-foreground",
        // Product categories
        profile: "[background:var(--color-brand-glow)] [color:var(--color-brand)]",
        idphoto: "[background:var(--color-info-bg)] [color:var(--color-info-text)]",
        corporate: "[background:var(--color-purple-bg)] [color:var(--color-purple-text)]",
        // Semantic pastels
        green: "[background:var(--color-success-bg)] [color:var(--color-success-text)]",
        red: "[background:var(--color-danger-bg)] [color:var(--color-danger-text)]",
        blue: "[background:var(--color-info-bg)] [color:var(--color-info-text)]",
        yellow: "[background:var(--color-warning-bg)] [color:var(--color-warning-text)]",
        purple: "[background:var(--color-purple-bg)] [color:var(--color-purple-text)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
